/* eslint-disable no-nested-ternary */
import { useState, useEffect } from 'react'
import { DeviceSettings } from './DeviceSettings'
import ExclamationCircle from '../../assets/svgs/exclamationCircle'
import Video from '../../assets/svgs/Video'
import CameraOn from '../../assets/svgs/cameraOn'
import X from '../../assets/svgs/X'
import Mic from '../../assets/svgs/mic'
import CameraOff from '../../assets/svgs/cameraOff'
import MicOff from '../../assets/svgs/MicOff'
import {
  DeviceControlAlertBox,
  DeviceControlBadge,
  DeviceControlCard,
  DeviceControlCenteredFlexContainer,
  DeviceControlFloatingControls,
  DeviceControlMainContainer,
  DeviceControlOutlineButton,
  DeviceControlOutlineControlBar,
  DeviceControlParagraph,
  DeviceControlRecordingAreaContainer,
  DeviceControlVideoContainer,
  DeviceControlVideoElement,
  DeviceControlVideoElementCover,
} from '../styled'
import CircleCheck from '../../assets/svgs/CheckCircle'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import useDeviceType from '../../hooks/useDeviceType'

export function DeviceControl({
  deviceState,
  setDeviceState,
  recordedVideo,
  videoRef,
}) {
  const { t } = useTranslation()
  const [showSettings, setShowSettings] = useState(false)
  const deviceType = useDeviceType()
  const isMobile = deviceType !== 'desktop'

  useEffect(() => {
    checkPermissions()
    navigator.permissions.query({ name: 'camera' }).then((result) => {
      result.onchange = () => checkPermissions()
    })
    navigator.permissions.query({ name: 'microphone' }).then((result) => {
      result.onchange = () => checkPermissions()
    })
  }, [])

  const checkPermissions = async () => {
    try {
      setDeviceState((prev) => ({
        ...prev,
        hasMicPermission: true,
        hasCameraPermission: true,
        permissionError: '',
      }))

      const devices = await navigator.mediaDevices.enumerateDevices()
      const mappedDevices = devices.map((device) => ({
        deviceId: device.deviceId,
        label: device.label,
        kind: device.kind,
      }))

      const defaultCamera = mappedDevices.find((d) => d.kind === 'videoinput')
      const defaultMic = mappedDevices.find((d) => d.kind === 'audioinput')
      const defaultSpeaker = mappedDevices.find((d) => d.kind === 'audiooutput')

      setDeviceState((prev) => ({
        ...prev,
        selectedCamera: defaultCamera?.deviceId || '',
        selectedMicrophone: defaultMic?.deviceId || '',
        selectedAudioOutput: defaultSpeaker?.deviceId || '',
      }))
    } catch (error) {
      setDeviceState((prev) => ({
        ...prev,
        hasMicPermission: false,
        hasCameraPermission: false,
        permissionError:
          error.message || 'Permission denied for camera and/or microphone',
        isMicEnabled: false,
        isCameraEnabled: false,
      }))
    }
  }

  const toggleMic = () => {
    if (!deviceState.hasMicPermission) {
      setDeviceState((prev) => ({
        ...prev,
        permissionError:
          'Please enable microphone permissions in your browser settings',
      }))
      return
    }
    setDeviceState((prev) => ({ ...prev, isMicEnabled: !prev.isMicEnabled }))
  }

  const toggleCamera = () => {
    if (!deviceState.hasCameraPermission) {
      setDeviceState((prev) => ({
        ...prev,
        permissionError:
          'Please enable camera permissions in your browser settings',
      }))
      return
    }
    setDeviceState((prev) => ({
      ...prev,
      isCameraEnabled: !prev.isCameraEnabled,
    }))
  }

  return (
    <DeviceControlMainContainer isMobile={isMobile}>
      <DeviceControlRecordingAreaContainer>
        {(deviceState.permissionError ||
          (!deviceState.isCameraEnabled && !deviceState.isMicEnabled) ||
          deviceState.isCameraEnabled !== deviceState.isMicEnabled) && (
          <DeviceControlAlertBox isMobile={isMobile}>
            <ExclamationCircle
              width={isMobile ? '16px' : '18px'}
              height={isMobile ? '16px' : '18px'}
            />
            <p className="m-0" style={{ fontSize: isMobile ? '12px' : '14px' }}>
              {deviceState.permissionError ||
                (!deviceState.isCameraEnabled && !deviceState.isMicEnabled
                  ? 'Please enable microphone and camera to start'
                  : deviceState.isCameraEnabled
                    ? 'Please enable microphone to proceed'
                    : 'Please enable camera to proceed')}
            </p>
          </DeviceControlAlertBox>
        )}
        <DeviceControlVideoContainer isMobile={isMobile}>
          {recordedVideo ? (
            <DeviceControlVideoElement
              isMobile={isMobile}
              src={recordedVideo}
              controls
              autoPlay
            />
          ) : deviceState.isCameraEnabled ? (
            <DeviceControlVideoElementCover
              isMobile={isMobile}
              ref={videoRef}
              autoPlay
              playsInline
              muted
            />
          ) : (
            <DeviceControlCenteredFlexContainer isMobile={isMobile}>
              <Video
                width={isMobile ? 45 : 55}
                height={isMobile ? 45 : 55}
                color="#4B5563"
              />
              <DeviceControlParagraph
                style={{ fontSize: isMobile ? '0.95rem' : '1.125rem' }}
              >
                {t('preInterview.enableCameraPreview')}
              </DeviceControlParagraph>
            </DeviceControlCenteredFlexContainer>
          )}
        </DeviceControlVideoContainer>

        <DeviceControlFloatingControls>
          <DeviceControlBadge>
            <CameraOn width={16} height={16} />
            {deviceState.hasCameraPermission ? (
              <CircleCheck width={16} height={16} />
            ) : (
              <X width={16} height={16} className="text-red-500" />
            )}
          </DeviceControlBadge>
          <DeviceControlBadge>
            <Mic width={16} height={16} />
            {deviceState.hasMicPermission ? (
              <CircleCheck width={16} height={16} />
            ) : (
              <X width={16} height={16} className="text-red-500" />
            )}
          </DeviceControlBadge>
        </DeviceControlFloatingControls>

        <div
          className="position-absolute"
          style={{
            top: '1rem',
            left: '1rem',
            zIndex: 60,
          }}
        >
          <DeviceSettings
            open={showSettings}
            onOpenChange={setShowSettings}
            isMobile={isMobile}
          />
        </div>
      </DeviceControlRecordingAreaContainer>

      <DeviceControlCard>
        <div className="w-100 d-flex justify-content-center">
          <DeviceControlOutlineControlBar>
            <DeviceControlOutlineButton
              isEnabled={deviceState.isCameraEnabled}
              onClick={toggleCamera}
              aria-label={
                deviceState.isCameraEnabled
                  ? 'Turn camera off'
                  : 'Turn camera on'
              }
            >
              {deviceState.isCameraEnabled ? <CameraOn /> : <CameraOff />}
            </DeviceControlOutlineButton>
            <DeviceControlOutlineButton
              isEnabled={deviceState.isMicEnabled}
              onClick={toggleMic}
              aria-label={
                deviceState.isMicEnabled
                  ? 'Turn microphone off'
                  : 'Turn microphone on'
              }
            >
              {deviceState.isMicEnabled ? <Mic /> : <MicOff />}
            </DeviceControlOutlineButton>
          </DeviceControlOutlineControlBar>
        </div>
      </DeviceControlCard>
    </DeviceControlMainContainer>
  )
}

DeviceControl.propTypes = {
  deviceState: PropTypes.shape({
    hasMicPermission: PropTypes.bool.isRequired,
    hasCameraPermission: PropTypes.bool.isRequired,
    isMicEnabled: PropTypes.bool.isRequired,
    isCameraEnabled: PropTypes.bool.isRequired,
    isRecording: PropTypes.bool.isRequired,
    permissionError: PropTypes.string,
  }),
  setDeviceState: PropTypes.func.isRequired,
  videoRef: PropTypes.shape({
    current: PropTypes.instanceOf(HTMLVideoElement),
  }).isRequired,
  recordedVideo: PropTypes.bool.isRequired,
}
